import { parseISO } from 'date-fns';
import { action, computed, makeObservable, observable } from 'mobx';
import { Award, AwardCountryStatistic, BatchCompletionSummary, batchCompletionSummary, BatchProgressSnapshot, BatchProgressStore, batchProcessedCount, COUNTRY_AWARD_MAX_WINNERS, Country, CountryNominationSummary, isBatchUiTerminal, NominationRole, ProgramCountryLead, ProgramCountryNominationReviewer, ProgramDate, AwardJudge, DropdownOption, blankAwardJudge } from '../../models';

export enum CountryAwardTabTypeCode {
  Nominations = 'nominations',
  Details = 'details',
  AiEval = 'ai-eval',
}

export class CountryAwardPageUiStore implements BatchProgressStore {
  @observable countryAward?: Award;

  @observable staffProgramDates: ProgramDate[] = [];

  @observable countryAwardStatistic?: AwardCountryStatistic;

  @observable programCountryLeads: ProgramCountryLead[] = [];

  @observable programCountryNominationReviewers: ProgramCountryNominationReviewer[] = [];

  @observable countryNominationSummaries: CountryNominationSummary[] = [];

  @observable selectedCountryNominationSummary?: CountryNominationSummary;

  @observable selectedNominationRole?: NominationRole;

  @observable selectedCountryAwardTabTypeCode: CountryAwardTabTypeCode = CountryAwardTabTypeCode.Nominations;

  @observable activeBatchRunId?: string;

  @observable batchProgress?: BatchProgressSnapshot;

  @observable isBatchStreaming = false;

  @observable isEvaluationRun?: boolean;

  @observable queuePosition?: number;

  @observable queueDepth?: number; // threaded from the DTO; intentionally stored-not-displayed

  @observable lastBatchSummary?: BatchCompletionSummary;

  @observable scrubDialogAuthorizedCountries: Country[] = [];

  @observable scrubDialogSelectedCountryId?: number;

  @observable scrubDialogSubmittedCount?: number;

  @observable awardJudges: AwardJudge[] = [];

  @observable isShowJudgeAssignmentModal = false;

  @observable judgeCapacityOptions: DropdownOption<number>[] = [];

  @observable selectedJudgeCapacityOptionValue?: number;

  @observable judgesToAdd: AwardJudge[] = [];

  constructor() {
    makeObservable(this);
  }

  @action
  resetStates(): void {
    this.countryAward = undefined;
    this.staffProgramDates = [];
    this.countryAwardStatistic = undefined;
    this.programCountryLeads = [];
    this.programCountryNominationReviewers = [];
    this.countryNominationSummaries = [];
    this.selectedCountryNominationSummary = undefined;
    this.selectedNominationRole = undefined;
    this.selectedCountryAwardTabTypeCode = CountryAwardTabTypeCode.Nominations;
    this.judgeCapacityOptions = [];
    this.activeBatchRunId = undefined;
    this.batchProgress = undefined;
    this.isBatchStreaming = false;
    this.isEvaluationRun = undefined;
    this.queuePosition = undefined;
    this.queueDepth = undefined;
    this.lastBatchSummary = undefined;
    this.scrubDialogAuthorizedCountries = [];
    this.scrubDialogSelectedCountryId = undefined;
    this.scrubDialogSubmittedCount = undefined;
    this.awardJudges = [];
    this.isShowJudgeAssignmentModal = false;
    this.judgesToAdd = [];
  }

  /* AI Scrubbing Batch */

  @action
  setActiveBatchRunId(batchRunId?: string): void {
    this.activeBatchRunId = batchRunId;
    if (batchRunId) this.lastBatchSummary = undefined; // a new batch clears the prior breakdown
  }

  @action
  setBatchProgress(batchProgress?: BatchProgressSnapshot): void {
    this.batchProgress = batchProgress;
  }

  @action
  setQueueMetrics(position?: number, depth?: number): void {
    this.queuePosition = position;
    this.queueDepth = depth;
  }

  @action
  setIsBatchStreaming(isBatchStreaming: boolean): void {
    this.isBatchStreaming = isBatchStreaming;
  }

  @action
  setIsEvaluationRun(isEvaluation?: boolean): void {
    this.isEvaluationRun = isEvaluation;
  }

  @action
  clearBatchProgress(): void {
    this.activeBatchRunId = undefined;
    this.batchProgress = undefined;
    this.isBatchStreaming = false;
    this.isEvaluationRun = undefined;
    this.queuePosition = undefined;
    this.queueDepth = undefined;
  }

  @action
  finalizeBatch(): void {
    if (this.batchProgress) {
      this.lastBatchSummary = batchCompletionSummary(this.batchProgress, this.isEvaluationRun ?? false);
    }
    this.clearBatchProgress();
  }

  @action
  clearLastBatchSummary(): void {
    this.lastBatchSummary = undefined;
  }

  @computed
  get isBatchInProgress(): boolean {
    return !!this.activeBatchRunId && !isBatchUiTerminal(this.batchProgress?.state);
  }

  @computed
  get batchProgressLabel(): string {
    const progress = this.batchProgress;
    if (progress?.state === 'Queued') {
      if (this.queuePosition == null) return 'Queued - waiting to start...';
      if (this.queuePosition === 1) return 'Queued - next to run';
      const ahead = this.queuePosition - 1;
      return `Queued - waiting behind ${ahead} batch${ahead === 1 ? '' : 'es'}`;
    }
    return progress ? `Processing ${batchProcessedCount(progress)}/${progress.total}...` : 'Processing...';
  }

  /* Scrub dialog (country selector + scoped count) */

  @action
  setScrubDialogAuthorizedCountries(countries: Country[]): void {
    this.scrubDialogAuthorizedCountries = countries;
  }

  @action
  setScrubDialogSelectedCountryId(countryId?: number): void {
    this.scrubDialogSelectedCountryId = countryId;
  }

  @action
  setScrubDialogSubmittedCount(count?: number): void {
    this.scrubDialogSubmittedCount = count;
  }

  @action
  resolveScrubDialogDefaultCountry(pageCountryId?: number): void {
    this.scrubDialogSelectedCountryId = this.scrubDialogAuthorizedCountries.some((country) => country.countryId === pageCountryId)
      ? pageCountryId
      : undefined;
  }

  @action
  resetScrubDialogCountryState(): void {
    this.scrubDialogAuthorizedCountries = [];
    this.scrubDialogSelectedCountryId = undefined;
    this.scrubDialogSubmittedCount = undefined;
  }

  @computed
  get scrubDialogSelectedCountryDisplayName(): string {
    return this.scrubDialogAuthorizedCountries.find((country) => country.countryId === this.scrubDialogSelectedCountryId)?.displayName ?? '';
  }

  @action
  setCountryAward(countryAward: Award): void {
    this.countryAward = countryAward;
    this.selectedJudgeCapacityOptionValue = countryAward.judgeCapacity;
  }

  @action
  setStaffProgramDates(staffProgramDates: ProgramDate[]): void {
    this.staffProgramDates = staffProgramDates;
  }

  @action
  setProgramCountryLeads(programCountryLeads: ProgramCountryLead[]): void {
    this.programCountryLeads = programCountryLeads;
  }

  @action
  setProgramCountryNominationReviewers(programCountryNominationReviewers: ProgramCountryNominationReviewer[]): void {
    this.programCountryNominationReviewers = programCountryNominationReviewers;
  }

  @action
  setCountryNominationSummaries(countryNominationSummaries: CountryNominationSummary[]): void {
    this.countryNominationSummaries = countryNominationSummaries;
  }

  @action
  setSelectedCountryAwardTabTypeCode(tabTypeCode: CountryAwardTabTypeCode): void {
    this.selectedCountryAwardTabTypeCode = tabTypeCode;
  }

  @action
  setJudgeCapacityOptions(options: DropdownOption<number>[]): void {
    this.judgeCapacityOptions = options;
  }

  @action
  setAwardJudges(awardJudges: AwardJudge[]): void {
    this.awardJudges = awardJudges;
  }

  @action
  setShowJudgeAssignmentModal(isShow: boolean): void {
    this.isShowJudgeAssignmentModal = isShow;
  }

  @action
  addAwardJudges(awardJudges: AwardJudge[]): void {
    this.awardJudges.push(...awardJudges);
  }

  @action
  setAwardJudgeStatus(judge: AwardJudge, status: boolean): void {
    if (judge.awardJudgeId === 0) {
      this.judgesToAdd[this.judgesToAdd.indexOf(judge)].judgingStarted = status;
      return;
    }
    this.awardJudges[this.awardJudges.indexOf(judge)].judgingStarted = status;
  }

  @action
  removeAwardJudge(judge: AwardJudge): void {
    if (judge.awardJudgeId === 0) {
      this.judgesToAdd.splice(this.judgesToAdd.indexOf(judge), 1);
      return;
    }
    this.awardJudges.splice(this.awardJudges.indexOf(judge), 1);
  }

  @action
  setJudgesToAdd(judgesToAdd: AwardJudge[]): void {
    this.judgesToAdd = judgesToAdd;
  }

  @action
  onAddJudges(judgeEmails: string[]): void {
    for (const email of judgeEmails) {
      const judge = blankAwardJudge();
      judge.awardId = this.countryAward?.awardId ?? 0;
      judge.emailAddress = email;
      this.judgesToAdd.push(judge);
    }
  }

  @computed
  get activeCountryAwardTabTypeCodes(): CountryAwardTabTypeCode[] {
    return [CountryAwardTabTypeCode.Nominations, CountryAwardTabTypeCode.Details];
  }

  @action
  setSelectedCountryNominationSummary(nominationSummary?: CountryNominationSummary): void {
    this.selectedCountryNominationSummary = nominationSummary;
  }

  @action
  setSelectedNominationRole(nominationRole?: NominationRole): void {
    this.selectedNominationRole = nominationRole;
  }

  @computed
  get selectedJudgeCapacityValue(): string {
    if (!this.selectedJudgeCapacityOptionValue) return '';
    return this.judgeCapacityOptions.find((option) => option.value === this.selectedJudgeCapacityOptionValue)?.text ?? '';
  }

  @computed
  get selectedJudgeCapacityOptions(): string[] {
    if (!this.selectedJudgeCapacityOptionValue) return [];
    return [String(this.selectedJudgeCapacityOptionValue)];
  }

  @computed
  get isWinnerAssigned(): boolean {
    const otherWinners = this.countryNominationSummaries.filter(
      (summary) => summary.role === 'Winner' && summary.nominationId !== this.selectedCountryNominationSummary?.nominationId,
    );
    return otherWinners.length >= COUNTRY_AWARD_MAX_WINNERS;
  }

  @computed
  get isScrubbingWindowAlreadyOpenButWinnersWindowNotStartYet(): boolean {
    const scrubbingWindow = this.staffProgramDates.find((date) => date.dateTypeCode === 'Scrubbing-Window');
    if (!scrubbingWindow) return false;

    const today = new Date();
    const scrubbingStartDate = parseISO(scrubbingWindow.startDate);

    if (today < scrubbingStartDate) return false;
    const winnersWindow = this.staffProgramDates.find((date) => date.dateTypeCode === 'Winners-Window');
    if (!winnersWindow) return true;

    const winnersStartDate = parseISO(winnersWindow.startDate);
    return today < winnersStartDate;
  }

  @action
  setCountryAwardStatistic(awardCountryStatistic: AwardCountryStatistic): void {
    this.countryAwardStatistic = awardCountryStatistic;
  }

  @computed
  get submittedNominationsCount(): number {
    return this.countryAwardStatistic?.numberOfSubmittedNominations ?? 0;
  }

  @computed
  get incompleteNominationsCount(): number {
    return this.countryAwardStatistic?.numberOfIncompleteNominations ?? 0;
  }

  @computed
  get passedNominationsCount(): number {
    return this.countryAwardStatistic?.numberOfPassedNominations ?? 0;
  }

  @computed
  get disqualifiedNominationsCount(): number {
    return this.countryAwardStatistic?.numberOfDisqualifiedNominations ?? 0;
  }

  @computed
  get pendingAffirmationNominationsCount(): number {
    return this.countryAwardStatistic?.numberOfPendingAffirmationNominations ?? 0;
  }

  @computed
  get activeJudgesCount(): number {
    return this.awardJudges.filter((judge) => judge.judgingStarted).length;
  }

  @computed
  get inactiveJudgesCount(): number {
    return this.awardJudges.filter((judge) => !judge.judgingStarted).length;
  }

  @computed
  get judgesActiveCount(): number {
    return this.countryAwardStatistic?.numberOfJudgesActive ?? 0;
  }

  @computed
  get judgesInActiveCount(): number {
    return this.countryAwardStatistic?.numberOfJudgesInActive ?? 0;
  }

  @computed
  get judgedNominationsCompletedCount(): number {
    return this.countryAwardStatistic?.numberOfJudgedNominationsCompleted ?? 0;
  }

  @computed
  get judgedNominationsInProgressCount(): number {
    return this.countryAwardStatistic?.numberOfJudgedNominationsInProgress ?? 0;
  }
}
